import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, Box, Typography, Stack, Divider, IconButton } from "@mui/material";
import { type Control, Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FoodsIcon, Button } from "@foodpilot/foods";
import { type CompanyWrite, useCreateOrganization } from "src/api/client-api/company/useCreateOrganization";
import { useGetOrganizationTypes } from "src/api/client-api/useOrganizationType";
import { CompanySettingsForm, CompanyGridFormZodSchema } from "./CompanySettingsForm";
import { useAssignPlatformOwner } from "src/api/client-api/company/useAssignPlatformOwner";
import {
    type CompanyGrid,
    useGetAllOrganization as useGetAllCompanyForOwner,
} from "src/api/client-api/company/useGetAllOrganization";
import { type Company, useGetOneOrganization } from "src/api/client-api/company/useGetOneOrganization.ts";
import { useGetSupplier } from "src/api/client-api/company/useGetSupplier";
import { useSaveSupplier } from "src/api/client-api/company/useSaveSupplier";
import { JsonAttributesField } from "src/components/JsonAttributes/JsonAttributesField";
import { CompanyTagField } from "./CompanyTagField";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    ConfirmationModalData,
    DataDeletionWarningDialog,
} from "src/components/Dialog/Templates/DataDeletionWarningDialog";
import { useGetCompanyPosts } from "src/api/client-api/post/useGetAllPosts";
import type { CompanyOption } from "src/components/Selector/CompanySelector";
import { SsqPostChangedWarnings } from "src/components/Dialog/Templates/AllWarnings/SsqPostChangedWarnings";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";
import { CompanyInformationSection } from "./CompanyInformationSection";
import { CompanyOrganizationSection } from "./CompanyOrganizationSection";
import { CompanyContactSection } from "./CompanyContactSection";

type CompanyModalProps = {
    open: boolean;
    onClose: () => void;
    company?: Company | CompanyGrid;
    isProvider?: boolean;
    onSuccess?: (company: any) => void;
};

export const CompanyModal = (props: CompanyModalProps) => {
    const { open, onClose, company, isProvider, onSuccess } = props;
    const isSupplierMode = isProvider === true;

    const [openConfirmationModal, setOpenConfirmationModal] = useState<ConfirmationModalData>({
        isOpen: false,
        errors: null,
    });

    // Cache to store selected parent companies as full objects (not just @id strings)
    const selectedParentsCache = useRef<Map<string, CompanyOption>>(new Map());

    const { data: organizationTypes = [], isLoading: isLoadingOrgTypes } = useGetOrganizationTypes();
    const { entitiesJsonAttributes, currentCampaign } = useFoodpilotContext();

    const organizationJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.organization] || [];

    // Check if the company has existing SSQ posts (only when editing an existing company)
    const { posts: existingPosts } = useGetCompanyPosts({
        entityId: company?.id,
        status: "calculation",
        extraParams: {
            "campaign.id": currentCampaign?.id,
            itemsPerPage: 1, // We only need to know if any exist
        },
    });

    // Fetch full company details when editing (to get address fields)
    const organizationDetails = useGetOneOrganization({
        companyId: company?.id?.toString(),
        // Only fetch when editing (company exists) and modal is open
        ...(!company || !open ? { companyId: undefined } : {}),
        enabled: !isSupplierMode,
    });
    const supplierDetails = useGetSupplier({
        supplierId: !company || !open ? undefined : company.id.toString(),
        enabled: isSupplierMode,
    });
    const { company: fullCompanyDetails, isLoading: isLoadingCompanyDetails } =
        isSupplierMode ? supplierDetails : organizationDetails;

    const createOrganization = useCreateOrganization();
    const saveSupplier = useSaveSupplier();
    const createCompany = isSupplierMode ? saveSupplier : createOrganization;
    const assignOwner = useAssignPlatformOwner();
    const { companies: allCompaniesForOwner = [] } = useGetAllCompanyForOwner({ enabled: !isSupplierMode });
    const alreadyHasOwner = allCompaniesForOwner?.some((c) => c.isPlatformOwner);
    const [platformOwnerChecked, setPlatformOwnerChecked] = useState(false);

    type Schema = z.infer<typeof CompanyGridFormZodSchema>;

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        getValues,
    } = useForm<Schema>({
        mode: "onChange",
        resolver: zodResolver(CompanyGridFormZodSchema),
    });

    const handleCompanyUpdate = (values: Schema) => {
        const companyWrite: CompanyWrite = CompanySettingsForm.getCompanyGridFormToCompanyWrite(values);

        const doPersist = () =>
            createCompany.mutate(
                { company: companyWrite },
                {
                    onSuccess: async (response) => {
                        // Tags are now handled automatically by the CompanyStateProcessor
                        // No need for separate tag sync call
                        const saved = response.data;
                        if (!isSupplierMode && platformOwnerChecked && !alreadyHasOwner) {
                            await assignOwner.mutateAsync(saved.id);
                        }
                        onSuccess?.(saved);
                        handleClose();
                    },
                },
            );

        doPersist();
    };

    const onSubmitHandler: SubmitHandler<Schema> = (values) => {
        // Only show SSQ deletion warning when editing an existing company AND actually changing the sector AND there are existing posts
        const isEditingExistingCompany = company !== undefined;
        const currentSector = company?.organizationSector?.["@id"];
        const newSector = values.organizationSector;
        const isSectorActuallyChanging =
            isEditingExistingCompany && currentSector !== newSector && newSector !== undefined && newSector !== "";
        const hasExistingPosts = existingPosts && existingPosts.length > 0;

        if (isSectorActuallyChanging && hasExistingPosts) {
            setOpenConfirmationModal({
                isOpen: true,
                errors: <SsqPostChangedWarnings />,
            });
            return;
        }
        handleCompanyUpdate(values);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Get default organization type
    const defaultOrgType = organizationTypes.find((orgType) => orgType.isDefault);

    // Use full company details when available, otherwise use the company prop
    const companyData = fullCompanyDetails || company;

    // Reset form when modal opens and we have the company data
    useEffect(() => {
        if (open && companyData && !isLoadingOrgTypes) {
            const orgTypeId = companyData.organizationType?.id?.toString() || defaultOrgType?.id?.toString() || "1";
            const companyParents = (companyData as any).parents?.map((parent: any) => parent["@id"]) || [];

            reset({
                id: companyData.id,
                name: companyData.name || "",
                registrationNumber: companyData.registrationNumber || "",
                externalId: companyData.externalId || "",
                address1: companyData.address1 || "",
                address2: companyData.address2 || "",
                city: companyData.city || "",
                zipcode: companyData.zipcode || "",
                state: companyData.state || "",
                country: companyData.country || "",
                phone: companyData.phone || "",
                email: companyData.email || "",
                clientId: companyData.clientId || "",
                isProvider: companyData.isProvider || false,
                organizationType: orgTypeId,
                organizationSector: companyData.organizationSector?.["@id"] || "",
                parents: companyParents,
                tags: companyData.tags && !Array.isArray(companyData.tags) ? companyData.tags : {},
                jsonAttributes: companyData.jsonAttributes || {},
            });
        } else if (open && !companyData && !isLoadingOrgTypes) {
            // Reset for creation (no ID)
            const orgTypeId = defaultOrgType?.id?.toString() || "1";

            reset({
                name: "",
                registrationNumber: "",
                externalId: "",
                address1: "",
                address2: "",
                city: "",
                zipcode: "",
                state: "",
                country: "",
                phone: "",
                email: "",
                clientId: "",
                isProvider: isProvider || false,
                organizationType: orgTypeId,
                organizationSector: "",
                parents: [],
                tags: {},
                jsonAttributes: {},
            });
        }
    }, [open, companyData, reset, defaultOrgType, isProvider, isLoadingOrgTypes]);

    // Additional effect to ensure form is cleared when switching to create mode
    useEffect(() => {
        if (open && !company && !isLoadingOrgTypes) {
            const orgTypeId = defaultOrgType?.id?.toString() || "1";

            reset({
                name: "",
                registrationNumber: "",
                externalId: "",
                address1: "",
                address2: "",
                city: "",
                zipcode: "",
                state: "",
                country: "",
                phone: "",
                email: "",
                clientId: "",
                isProvider: isProvider || false,
                organizationType: orgTypeId,
                organizationSector: "",
                parents: [],
                tags: {},
                jsonAttributes: {},
            });
        }
    }, [open, company, reset, defaultOrgType, isProvider, isLoadingOrgTypes]);

    // Show loading if organization types are still loading or if we're loading company details
    if (isLoadingOrgTypes || (company && isLoadingCompanyDetails)) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    maxHeight: "90vh",
                    width: "638px",
                },
            }}
            sx={{
                overflow: "scroll",
            }}
        >
            <DataDeletionWarningDialog
                confirmationData={openConfirmationModal}
                setConfirmationData={setOpenConfirmationModal}
                onConfirm={() => {
                    const values = getValues();
                    handleCompanyUpdate(values);
                }}
            />
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px 32px 0px 32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                        {company ? "Edit company" : "Add a company"}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        padding: "8px",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1.5} />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                    overflowY: "auto",
                }}
            >
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing="24px">
                        <CompanyInformationSection
                            control={control}
                            errors={errors}
                            isSupplierMode={isSupplierMode}
                            alreadyHasOwner={alreadyHasOwner}
                            platformOwnerChecked={platformOwnerChecked}
                            setPlatformOwnerChecked={setPlatformOwnerChecked}
                        />

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        <CompanyOrganizationSection
                            control={control}
                            errors={errors}
                            organizationTypes={organizationTypes}
                        />

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        <CompanyContactSection
                            control={control}
                            errors={errors}
                            company={company}
                            companyData={companyData}
                            isSupplierMode={isSupplierMode}
                            selectedParentsCache={selectedParentsCache}
                        />

                        {/* JSON Attributes Section */}
                        {organizationJsonAttributes.length > 0 && (
                            <>
                                <Divider sx={{ borderColor: "#E7E6E4" }} />
                                <Box>
                                    <JsonAttributesField
                                        control={control as Control<any>}
                                        jsonAttributes={organizationJsonAttributes}
                                    />
                                </Box>
                            </>
                        )}

                        {/* Tags Section */}
                        <Divider sx={{ borderColor: "#E7E6E4" }} />
                        <Box>
                            <Controller
                                name="tags"
                                control={control}
                                render={({ field }) => (
                                    <CompanyTagField
                                        company={{ id: company?.id, tags: field.value || {} }}
                                        setCompany={(updatedCompany) => {
                                            field.onChange(updatedCompany.tags);
                                        }}
                                        field={field}
                                        isProvider={isProvider}
                                    />
                                )}
                            />
                        </Box>

                        {/* Hidden fields for compatibility */}
                        <Controller
                            name="id"
                            control={control}
                            render={({ field: { ref, ...field } }) => <input type="hidden" {...field}></input>}
                        />
                        <Controller
                            name="isProvider"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <input type="hidden" {...field} value={field.value ? "true" : "false"}></input>
                            )}
                        />
                    </Stack>
                </form>
            </DialogContent>

            {/* Footer with buttons */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "28px 32px",
                    borderTop: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box /> {/* Empty box for spacing */}
                <Box sx={{ display: "flex", gap: "12px" }}>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        // sx={{
                        //     borderRadius: "44px",
                        //     padding: "0px 20px",
                        //     height: "40px",
                        //     fontWeight: 700,
                        //     fontSize: "0.875rem",
                        //     color: "#393A36",
                        //     borderColor: "#E7E6E4",
                        //     backgroundColor: "linear-gradient(180deg, #FFFFFF 0%, #F6F5F3 100%)",
                        //     "&:hover": {
                        //         backgroundColor: "linear-gradient(180deg, #FFFFFF 0%, #F6F5F3 100%)",
                        //     },
                        // }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        onClick={handleSubmit(onSubmitHandler)}
                        disabled={!isValid || createCompany.isLoading}
                        // sx={(theme) => ({
                        //     borderRadius: "44px",
                        //     padding: "0px 20px",
                        //     height: "40px",
                        //     fontWeight: 600,
                        //     fontSize: "0.875rem",
                        //     background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        //     color: "white !important",
                        //     boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        //     "&:hover": {
                        //         background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                        //         boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                        //         color: "white !important",
                        //     },
                        //     "&:disabled": {
                        //         background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        //         opacity: 0.36,
                        //         color: "white !important",
                        //     },
                        // })}
                    >
                        {company ? "Save" : "Add"}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};
